
import React, { useEffect, useRef, useState } from 'react';
import { initBlockStackGame } from '../games/blockstack';
import { useIsMobile } from '@/hooks/use-mobile';
import GameLoading from './games/asteroids/components/GameLoading';
import GameError from './games/asteroids/components/GameError';
import MobileInstructions from './games/common/MobileInstructions';
import { useToast } from './ui/use-toast';

interface BlockStackGameProps {
  title: string;
}

const BlockStackGame = ({ title }: BlockStackGameProps) => {
  const gameContainerRef = useRef<HTMLDivElement>(null);
  const gameContainerId = 'blockstack-game-container';
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [initAttempts, setInitAttempts] = useState(0);
  const maxAttempts = 3;
  const isMobile = useIsMobile();
  const gameInitialized = useRef(false);
  const { toast } = useToast();
  const phaserLoaded = useRef(false);

  // Load Phaser first
  useEffect(() => {
    const loadPhaser = async () => {
      if (typeof window.Phaser === 'undefined') {
        try {
          console.log('Loading Phaser from CDN...');
          // Clear any existing Phaser script to avoid conflicts
          const existingScript = document.getElementById('phaser-script');
          if (existingScript) {
            existingScript.remove();
          }
          
          // Create a new script element with an ID for easy reference
          const script = document.createElement('script');
          script.id = 'phaser-script';
          script.src = 'https://cdn.jsdelivr.net/npm/phaser@3.55.2/dist/phaser.min.js';
          script.async = true;
          
          // Wait for the script to load
          const scriptLoaded = new Promise<void>((resolve, reject) => {
            script.onload = () => {
              console.log('Phaser library loaded successfully');
              phaserLoaded.current = true;
              resolve();
            };
            script.onerror = (e) => {
              console.error('Failed to load Phaser library:', e);
              reject(new Error('Failed to load the game engine. Please check your internet connection and try again.'));
            };
          });
          
          // Add the script to the document
          document.head.appendChild(script);
          
          try {
            await scriptLoaded;
          } catch (err) {
            setError('Could not load the game engine. Please try refreshing the page.');
            setIsLoading(false);
            throw err;
          }
        } catch (err) {
          console.error('Error in Phaser loading process:', err);
          setError('Could not load the game engine. Please try refreshing the page.');
          setIsLoading(false);
        }
      } else {
        console.log('Phaser already available on window');
        phaserLoaded.current = true;
      }
    };
    
    loadPhaser();
  }, []);

  // Initialize the game when Phaser is loaded
  useEffect(() => {
    let isMounted = true;
    let initTimer: ReturnType<typeof setTimeout>;
    
    const initGame = async () => {
      // Skip if Phaser isn't loaded yet
      if (!phaserLoaded.current) {
        console.log('Waiting for Phaser to load...');
        return;
      }
      
      if (gameContainerRef.current && typeof window.Phaser !== 'undefined') {
        try {
          setIsLoading(true);
          setError(null);
          
          console.log('Starting to initialize BlockStack game, attempt:', initAttempts + 1);
          await initBlockStackGame(gameContainerId);
          gameInitialized.current = true;
          
          if (isMounted) {
            console.log('BlockStack game initialized successfully');
            setIsLoading(false);
            toast({
              title: "Game Ready",
              description: "Block Stack is ready to play!",
              duration: 3000
            });
          }
        } catch (error) {
          console.error('Failed to initialize BlockStack game:', error);
          
          if (isMounted) {
            if (initAttempts < maxAttempts - 1) {
              console.log(`Retrying game initialization (${initAttempts + 1}/${maxAttempts})...`);
              setInitAttempts(prev => prev + 1);
            } else {
              setError('Failed to load the BlockStack game. Please try refreshing the page.');
              setIsLoading(false);
            }
          }
        }
      } else if (isMounted && (typeof window.Phaser === 'undefined' || !gameContainerRef.current)) {
        const reason = !gameContainerRef.current 
          ? 'Game container not found' 
          : 'Game engine not loaded';
        
        console.error(`Cannot initialize game: ${reason}`);
        
        if (initAttempts < maxAttempts - 1) {
          console.log(`Waiting and retrying (${initAttempts + 1}/${maxAttempts})...`);
          setInitAttempts(prev => prev + 1);
        } else {
          setError(`${reason}. Please try refreshing the page.`);
          setIsLoading(false);
        }
      }
    };

    // Only try to initialize if Phaser is loaded
    if (phaserLoaded.current && !gameInitialized.current && initAttempts < maxAttempts) {
      // Short delay to ensure DOM is ready and Phaser is fully initialized
      initTimer = setTimeout(() => {
        initGame();
      }, 1000);
    }

    // Handle resize events for responsiveness
    const handleResize = () => {
      if (window.blockStackGame && window.blockStackGame.scale) {
        // Update game scale to fit the container
        try {
          window.blockStackGame.scale.refresh();
        } catch (e) {
          console.error('Error resizing BlockStack game:', e);
        }
      }
    };
    
    window.addEventListener('resize', handleResize);

    // Cleanup when the component unmounts
    return () => {
      isMounted = false;
      if (initTimer) clearTimeout(initTimer);
      window.removeEventListener('resize', handleResize);
      
      if (window.blockStackGame) {
        try {
          // Make sure we properly shut down the active scene before destroying the game
          if (window.blockStackGame.scene && window.blockStackGame.scene.scenes) {
            window.blockStackGame.scene.scenes.forEach((scene: any) => {
              if (scene.scene && scene.scene.isActive()) {
                try {
                  scene.shutdown();
                } catch (e) {
                  console.error('Error shutting down scene:', e);
                }
              }
            });
          }
          
          window.blockStackGame.destroy(true, true);
          window.blockStackGame = null;
          gameInitialized.current = false;
        } catch (e) {
          console.error('Error destroying BlockStack game:', e);
        }
      }
    };
  }, [initAttempts, maxAttempts, gameContainerId, toast, phaserLoaded.current]);

  const handleRetry = () => {
    if (window.blockStackGame) {
      try {
        window.blockStackGame.destroy(true, true);
        window.blockStackGame = null;
        gameInitialized.current = false;
      } catch (e) {
        console.error('Error destroying BlockStack game during retry:', e);
      }
    }
    
    // If Phaser wasn't loaded, reload the page to ensure a clean state
    if (!phaserLoaded.current) {
      window.location.reload();
    } else {
      setIsLoading(true);
      setError(null);
      setInitAttempts(0);
    }
  };

  // If there's an error, show an error component
  if (error) {
    return <GameError errorMessage={error} onRetry={handleRetry} />;
  }

  return (
    <div 
      className="w-full h-full flex flex-col items-center justify-center relative"
      role="application"
      aria-label="BlockStack Game"
    >
      {isLoading && <GameLoading title="BlockStack" />}
      
      <div 
        id={gameContainerId}
        ref={gameContainerRef} 
        className="w-full h-full flex items-center justify-center"
        aria-label="BlockStack game container"
      />
      
      {isMobile && (
        <MobileInstructions 
          gameType="BlockStack" 
          className="absolute bottom-0 left-0 right-0 bg-yellow-50 p-3 text-sm text-center shadow-lg z-10"
        />
      )}
    </div>
  );
};

export default BlockStackGame;
